import Card from "components/card";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "components/pagination/Pagination";
import EventService from "services/events.service";
import EventTypesService from "services/eventTypes.service";
import { FiSearch } from "react-icons/fi";
import * as yup from "yup";
import moment from "moment";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { useFormik } from "formik";
import InputField from "components/fields/InputField";
import Select from "react-select";
import { AiFillEdit } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const EVENT_AND_CAR_TYPES_CONFIG = {
  event: {
    idField: "event_type_id",
    fetch: (body) => EventService.getAllCategory(body),
    create: (payload) => EventService.createCategory(payload),
    update: (payload) => EventService.updateCategory(payload),
    delete: (id) =>
      EventService.deleteCategoryEvent({
        event_type_id: id,
      }),
    deleteSuccessMessage: "Event type deleted successfully!",
    createSuccessMessage: "Event type created successfully!",
    updateSuccessMessage: "Event type updated successfully!",
    modalTitle: "Add Event Type",
    fieldLabel: "Event Type Name*",
    addButtonLabel: "+ Add Event Type",
    deletePrompt: "Are you sure you want to delete this event type?",
  },
  car: {
    idField: "car_type_id",
    fetch: (body) => EventTypesService.getAllEventTypes(body),
    create: (payload) => EventTypesService.createEventTypes(payload),
    update: (payload) => EventTypesService.updateEvent(payload),
    delete: (id) =>
      EventTypesService.deleteCategoryEvent({
        car_type_id: id,
      }),
    deleteSuccessMessage: "Car type deleted successfully!",
    createSuccessMessage: "Car type created successfully!",
    updateSuccessMessage: "Car type updated successfully!",
    modalTitle: "Add Car Type",
    fieldLabel: "Car Type Name*",
    addButtonLabel: "+ Add Car Type",
    deletePrompt: "Are you sure you want to delete this car type?",
  },
};

const EventAndCarTypes = () => {
  const profileRef = useRef();
  const modalRef = useRef();
  const [activeType, setActiveType] = useState("event");
  const [sorting, setSorting] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  // Initialize from cache - show loader only on first load (will be set per tab)
  const prevSearchTextRef = useRef("");
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [id, setId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isTypeChanging, setIsTypeChanging] = useState(false);

  
  // Track which tabs have been loaded to prevent re-fetching
  const [hasFetchedEventTypes, setHasFetchedEventTypes] = useState(false);
  const [hasFetchedCarTypes, setHasFetchedCarTypes] = useState(false);
  // Track previous activeType to detect tab switches
  const prevActiveTypeRef = useRef(activeType);

  const currentConfig = useMemo(
    () => EVENT_AND_CAR_TYPES_CONFIG[activeType],
    [activeType]
  );

  const colourStyles = {
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: "rgb(30 30 32 / 1) !important",
        ":hover": {
          backgroundColor: "rgba(255, 255, 255, 0.30) !important",
          color: "#ffffff",
        },
      };
    },
    control: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "9999px",
      borderColor: "rgba(255, 255, 255, 0.25)",
      
      boxShadow: "none",
      outline: "none",
      paddingLeft: "8px",
      ...(isFocused
        ? {
            borderColor: "rgba(255, 255, 255, 0.35)",
          }
        : {}),
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "18px",
      overflow: "hidden",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#ffffff",
    }),
  };

  const typeOptions = [
    { label: "Event Types", value: "event" },
    { label: "Car Types", value: "car" },
  ];

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  // Initialize from cache if available (will be updated based on activeType)
  const [data, setData] = useState(() => []);

  // Track previous filter values to detect actual changes (per tab)
  const prevFiltersRef = React.useRef({
    event: null, // null means not initialized yet
    car: null
  });

  const fetchData = useCallback(async (isSearching = false) => {
    // Get cache key based on active type
    const cacheKey = activeType === "event" ? "eventTypes" : "carTypes";
    const isFirstLoad = tabCache[cacheKey].firstLoad;
    
    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging || isTypeChanging) {
      setIsLoading(true);
    }
    
    
    try {
      const body = {
        pagination: {
          page,
          pageSize,
          projection: {},
          filters: {},
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
      };
      const response = await currentConfig.fetch(body);
      const typesData = response.data.data;
      
      // Update state and cache
      setData(typesData);
      tabCache[cacheKey].data = typesData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache[cacheKey].firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to load data.");
      const emptyData = [];
      setData(emptyData);
      const cacheKey = activeType === "event" ? "eventTypes" : "carTypes";
      tabCache[cacheKey].data = emptyData;
      if (isFirstLoad) {
        tabCache[cacheKey].firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isTypeChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false); // 👈 STOP pagination loader
      setIsTypeChanging(false);
    }
    
  }, [currentConfig, page, pageSize, searchText, activeType, isPageChanging, isTypeChanging]);

  // Fetch data when tab is first opened or when filters change
  useEffect(() => {
    // Get cache key based on active type
    const cacheKey = activeType === "event" ? "eventTypes" : "carTypes";
    const cachedData = tabCache[cacheKey].data;
    
    // Detect tab switch
    const isTabSwitch = prevActiveTypeRef.current !== activeType;
    prevActiveTypeRef.current = activeType;
    
    // Initialize data from cache if available when switching tabs
    if (cachedData) {
      if (activeType === "event" && !hasFetchedEventTypes) {
        setData(cachedData);
      } else if (activeType === "car" && !hasFetchedCarTypes) {
        setData(cachedData);
      } else if (isTabSwitch) {
        // Show cached data immediately when switching tabs
        setData(cachedData);
      }
    }
    
    const prevFilters = prevFiltersRef.current[activeType];
    
    // Check if search text changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // Check if filters changed (only if we have previous values to compare)
    const filtersChanged = prevFilters !== null && (
      page !== prevFilters.page ||
      searchText !== prevFilters.searchText
    );
    
    // Determine if this tab has been loaded
    const hasFetched = activeType === "event" ? hasFetchedEventTypes : hasFetchedCarTypes;
    
    // Always fetch when switching tabs, or if not fetched yet, or if filters changed
    if (isTabSwitch || !hasFetched || filtersChanged) {
      // Show loader if this is a search operation and we have cached data
      // Don't show loader when switching tabs if we have cached data (better UX)
      const isSearching = isSearchChange && cachedData && !isTabSwitch;
      fetchData(isSearching);
      
      // Mark tab as loaded
      if (!hasFetched) {
        if (activeType === "event") {
          setHasFetchedEventTypes(true);
        } else {
          setHasFetchedCarTypes(true);
        }
      }
      
      // Update previous filter values after fetch
      prevFiltersRef.current[activeType] = {
        page,
        searchText
      };
    }
    // eslint-disable-next-line
  }, [activeType, page, pageSize, searchText, fetchData, hasFetchedEventTypes, hasFetchedCarTypes]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };
  
  

  const formik = useFormik({
    initialValues: {
      type: "",
      event_type_id: "",
      car_type_id: "",
    },
    validationSchema: yup.object().shape({
      type: yup.string().required("Please enter a name"),
    }),
    onSubmit: async (values) => {
      setIsActionLoading(true);
      try {
        const idField = currentConfig.idField;
        const payload = {
          type: values.type?.trim(),
        };
        if (values[idField]) {
          payload[idField] = values[idField];
        }

        let response;
        if (values[idField]) {
          response = await currentConfig.update(payload);
          Toast(
            "success",
            response?.data?.message || currentConfig.updateSuccessMessage
          );
        } else {
          response = await currentConfig.create(payload);
          Toast(
            "success",
            response?.data?.message || currentConfig.createSuccessMessage
          );
        }

        setProfilePopup(false);
        document.body.style.overflow = "unset";
        formik.resetForm();
        fetchData();
      } catch (error) {
        Toast(
          "error",
          error.response?.data?.message || "Failed to save the record."
        );
      } finally {
        setIsActionLoading(false);
      }
    },
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setPopupOpenDelete(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPage(1);
    setSearchInputValue("");
    setSearchText("");
    prevSearchTextRef.current = ""; // Reset search text ref when switching tabs
    setId("");
    setProfilePopup(false);
    setPopupOpenDelete(false);
    formik.resetForm();
  }, [activeType]); // intentionally exclude formik to avoid unnecessary resets

  const openCreateModal = useCallback(() => {
    formik.resetForm();
    setId("");
    setProfilePopup(true);
    document.body.style.overflow = "hidden";
  }, [formik]);

  const handleEditOpen = useCallback(
    (item) => {
      if (!item) return;
      formik.setValues({
        type: item?.type || "",
        event_type_id: activeType === "event" ? item?._id : "",
        car_type_id: activeType === "car" ? item?._id : "",
      });
      setId(item._id);
      setProfilePopup(true);
      document.body.style.overflow = "hidden";
    },
    [activeType, formik]
  );

  const handleOpenDelete = useCallback((itemId) => {
    setId(itemId);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("index", {
        id: "index",
        size: 60,
        header: () => (
          <p className="text-sm font-bold uppercase text-white">
            Number
          </p>
        ),
        cell: (info) => (
          <div className="flex items-center break-all">
            {info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor("type", {
        id: "type",
        size: 150,
        header: () => (
          <p className="text-sm font-bold uppercase text-white">
            Type
          </p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-white">
            {info.getValue() ? info.getValue() : "--"}
          </p>
        ),
      }),
      columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 180,
        header: () => (
          <p className="text-sm font-bold uppercase text-white">
            Created Date
          </p>
        ),
        cell: (info) => (
          <p className="text-sm text-white">
            {info.getValue()
              ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
              : "--"}
          </p>
        ),
      }),
      columnHelper.accessor("action", {
        id: "action",
        size: 120,
        header: () => (
          <p className="text-sm font-bold uppercase text-white text-end">Action</p>
        ),
        cell: (info) => {
          const rowId = info.row.original._id;
          const isEventType = activeType === "event";
          return (
            <div className="action-icons-container flex gap-3 justify-end">
              <div
                className="action-icon-btn ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 hover:border-white/80 hover:text-white/80"
                onClick={() => handleEditOpen(info.row.original)}
              >
                {isActionLoading && rowId === id ? (
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-4 w-4"
                  />
                ) : (
                  <AiFillEdit />
                )}
              </div>
              <div
                className="action-icon-btn ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] duration-150"
                onClick={() => handleOpenDelete(rowId)}
              >
                {isActionLoading && rowId === id ? (
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-4 w-4"
                  />
                ) : (
                  <MdDeleteForever size={20} />
                )}
              </div>
            </div>
          );
        },
      }),
    ],
    [handleEditOpen, handleOpenDelete, id, isActionLoading, activeType]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const handleDelete = async () => {
    setIsActionLoading(true);
    try {
      await currentConfig.delete(id);
      Toast("success", currentConfig.deleteSuccessMessage);
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      fetchData();
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to delete the record."
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const cardExtra = `w-full h-full px-6 pb-6 ${
    isLoading ? "" : "md:overflow-y-auto overflow-x-auto"
  }`;
  const tableWrapperClass = `table-scroll relative mt-4 -mx-6 ${
    isLoading 
      ? "xxs:h-[40vh] xs:h-[55vh] sm:h-[70vh] overflow-hidden" 
      : "overflow-x-auto md:overflow-y-auto md:max-h-[60vh]"
  }`;

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <style>
        {`
          @media (max-width: 1023px) {
            .equal-width-columns {
              table-layout: fixed !important;
            }
            .equal-width-columns th,
            .equal-width-columns td {
              width: 25% !important;
            }
          }




            @media (min-width: 500px) and (max-width: 639px) {
            .search-input-container {
              max-width: 140px !important;
            }
            .search-input-field {
            width: 100px !important;
            }
          }
          @media (max-width: 639px) {
            .select__single-value {
              font-size: 0.80rem !important;
            }
            .select__control {
              font-size: 0.75rem !important;
            }
            .select__placeholder {
              font-size: 0.75rem !important;
            }
            .add-event-btn {
              font-size: 0.80rem !important;
            }
          }
          @media (min-width: 500px) and (max-width: 575px) {
            .events-header-container {
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              gap: 0.5rem !important;
            }
            .events-header-container > div:first-child {
              width: auto !important;
              flex-shrink: 1 !important;
            }
            .events-header-container .search-input-container {
              max-width: 130px !important;
              width: 130px !important;
              min-width: 130px !important;
            }
            .events-header-container .search-input-field {
              max-width: 70px !important;
              width: 70px !important;
            }
            .events-header-container .default {
              width: auto !important;
              flex-shrink: 0 !important;
              justify-content: flex-end !important;
            }
            .events-header-container .events-select-dropdown {
              width: 110px !important;
              min-width: 140px !important;
            
            }
            .events-header-container .add-event-btn {
              max-width: 120px !important;
              flex-shrink: 0 !important;
            }
          }
          .table-scroll {
            overflow-x: auto !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .table-scroll table {
            min-width: 100%;
            width: 100%;
          }
          @media (max-width: 767px) {
            .table-scroll {
              overflow-x: auto !important;
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            .table-scroll table {
              min-width: 600px !important;
              width: auto !important;
            }
          }

        `}
      </style>
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={cardExtra}>
          <div className="events-header-container mt-4 -ml-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex w-full items-center sm:w-auto">
              <div className="search-input-container flex items-center rounded-full bg-navy-900 py-3 text-white xl:w-[180px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="search-input-field block w-full bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none placeholder:!text-white sm:w-fit"
                  onChange={handleSearchInputChange}
                  value={searchInputValue}
                />
              </div>
            </div>
            <div className="default flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end -mr-3">
              <div className="flex items-center sm:w-auto">
                <Select
                  className="events-select-dropdown w-full sm:w-[160px] !text-white"
                  classNamePrefix="select"
                  defaultValue={typeOptions[0]}
                  options={typeOptions}
                  value={typeOptions.find((option) => option.value === activeType)}
                  onChange={(option) => {
                    if (!option) return;
                    setIsTypeChanging(true);
                    setActiveType(option.value);
                  }}
                  styles={colourStyles}
                  isSearchable={false}
                />
              </div>
              <button
                type="button"
                className="add-event-btn h-11 w-fit max-w-[120px] whitespace-nowrap rounded-full bg-orangePrimary px-3 text-sm text-white duration-150 hover:bg-orangePrimary/80 sm:max-w-none sm:px-4 sm:text-base"
                onClick={openCreateModal}
              >
                {currentConfig.addButtonLabel}
              </button>
            </div>
          </div>

          <div className={tableWrapperClass}>
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full equal-width-columns ${isLoading ? "table-fixed h-full" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%", height: "100%" }
                  : { tableLayout: "auto", minWidth: "800px" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const isCreatedDate = header.id === 'createdAt';
                      const isType = header.id === 'type';

                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pt-4 pl-4 pr-4 md:pl-4 md:pr-4 ${isLast ? 'text-end' : 'text-start'} ${isFirst ? 'lg:pl-6 lg:pr-4' : isLast ? 'lg:pl-2 lg:pr-48' : isCreatedDate ? 'lg:pl-0 lg:pr-2' : isType ? 'lg:pl-4 lg:pr-0' : 'lg:pl-4 lg:pr-4'}`}
                          style={{ width: isFirst ? '8%' : isLast ? '20%' : isCreatedDate ? '30%' : '9%' }}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="md:hidden">
                    <td colSpan={columns.length} className="py-6">
                      <div className="flex justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const isFirst = cellIndex === 0;
                        const isLast = cellIndex === row.getVisibleCells().length - 1;
                        const isCreatedDate = cell.column.id === 'createdAt';
                        const isType = cell.column.id === 'type';
                        return (
                          <td
                            key={cell.id}
                            className={`${isType ? 'max-w-[120px] lg:max-w-none' : 'max-w-[200px] lg:max-w-none'} overflow-hidden text-ellipsis whitespace-nowrap border-white/0 py-3 pl-4 pr-4 md:pl-4 md:pr-4 ${isFirst ? 'lg:pl-6 lg:pr-2' : isLast ? 'lg:pl-2 lg:pr-44' : isCreatedDate ? 'lg:pl-0 xl:pr-0' : isType ? 'xl:pl-4 xl:pr-0' : 'xl:pl-4 xl:pr-2'}`}
                            style={{ width: isFirst ? '8%' : isLast ? '15%' : isCreatedDate ? '25%' : '8%' }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}

          {popupOpenDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={modalRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
                <p className="mb-6 text-gray-300">{currentConfig.deletePrompt}</p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpenDelete(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {isActionLoading ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {profilePopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="mx-3 w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={profileRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">
                  {currentConfig.modalTitle}
                </h2>
                <form>
                  <div className="mb-3">
                    <InputField
                      variant="auth"
                      extra="mb-2"
                      label={currentConfig.fieldLabel}
                      placeholder="Type Name"
                      id="type"
                      type="text"
                      name="type"
                      onChange={formik.handleChange}
                      value={formik.values.type}
                    />
                    {formik.touched.type && formik.errors.type && (
                      <p className="validation-error">{formik.errors.type}</p>
                    )}
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => {
                        setProfilePopup(false);
                        formik.resetForm();
                        document.body.style.overflow = "unset";
                      }}
                      type="button"
                      className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={formik.handleSubmit}
                      className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                    >
                      {isActionLoading ? (
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="mx-auto h-6 w-6"
                        />
                      ) : formik.values[currentConfig.idField] ? (
                        "Update"
                      ) : (
                        "Add"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default EventAndCarTypes;

